import React, { ChangeEventHandler, MouseEventHandler } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { css } from '@emotion/react';
import MuiCheckIcon from '@mui/icons-material/Check';
import MuiCloseIcon from '@mui/icons-material/Close';
import MuiEmailIcon from '@mui/icons-material/Email';
import MuiPersonIcon from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import MuiFormControl from '@mui/material/FormControl';
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiInput from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import MuiInputLabel from '@mui/material/InputLabel';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';

import styles from '@/components/InviteMember/InviteMember.module.scss';
import { AccessLevelPicker } from '@/components/Steps/InviteTeamStep/AccessLevelPicker';
import type { InviteOptimizationVariantId } from '@/components/Steps/InviteTeamStep/inviteStepOptimization.util';
import { FlexBox } from '@/shared/FlexBox';
import { TextField } from '@/shared/TextField';

import { AccessLevel, Member } from '../../types';

type MemberFieldVariantProps = {
  accessLvl: AccessLevel;
  inputId: string;
  onAccessLvlChange: (accessLvl: AccessLevel) => void;
  onInputClick: MouseEventHandler<HTMLInputElement>;
  onInputChange: ChangeEventHandler<HTMLInputElement>;
  inviteOptimizationVariant?: InviteOptimizationVariantId;
  allowedAccessLevels?: AccessLevel[];
  required?: boolean;
  member?: Member;
};

export type MemberFieldProps = {
  inputId: string;
  member: Member;
  variant: 'legacy' | 'exp';
  onAccessLvlChange: (accessLvl: AccessLevel) => void;
  onInputClick?: InviteMemberTypes.InputClickFn;
  onInputChange?: InviteMemberTypes.InputChangeFn;
  /** When B or C, shows valid/invalid email icons on the invite step experiment. */
  inviteOptimizationVariant?: InviteOptimizationVariantId;
  /** When set, limits access level options (e.g. automatic tracking: manager and admin only). */
  allowedAccessLevels?: AccessLevel[];
  required?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const EMAIL_PATTERN_REGEX = /^[^@\s]+@([-\w]+\.)+[-\w]{2,4}$/g;

const EMAIL_STATUS_ICON_PX = 20;

const EmailValidationStatusAdornment: React.FC<{ status: 'valid' | 'invalid' }> = ({ status }) => {
  const isValid = status === 'valid';
  return (
    <Box
      role="img"
      aria-label={ isValid ? 'Email is valid' : 'Email is invalid' }
      sx={{
        width: EMAIL_STATUS_ICON_PX,
        height: EMAIL_STATUS_ICON_PX,
        borderRadius: '50%',
        backgroundColor: isValid ? '#02AB50' : '#E53935',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      { isValid ? (
        <MuiCheckIcon sx={{ fontSize: 14, color: '#fff' }} />
      ) : (
        <MuiCloseIcon sx={{ fontSize: 14, color: '#fff' }} />
      ) }
    </Box>
  );
};

const muiFormControlStyles = {
  root: css`
    & .MuiInputLabel-root {
      transform: translate(0, 10px) scale(1);
    }

    & .MuiInput-root::after, & .MuiInput-root:hover::before {
      border-bottom: 0 !important;
    }
  `,
};


export const MemberField: React.FC<MemberFieldProps> = (
  {
    inputId,
    member,
    onInputClick = () => ({}),
    onInputChange = () => ({}),
    onAccessLvlChange,
    variant,
    inviteOptimizationVariant,
    allowedAccessLevels,
    ...restProps
  },
) => {
  const { getValues, setValue } = useFormContext();

  const handleInputClick = () =>
    onInputClick({ email: getValues(inputId), role: member.role, id: member.id });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setValue(inputId, target.value, { shouldDirty: true, shouldValidate: true });

    onInputChange(inputId, { email: target.value, role: member.role, id: member.id });
  };

  return variant === 'legacy' ? (
    <LegacyMemberField
      inputId={ inputId }
      required={ restProps.required }
      accessLvl={ member.role }
      onInputClick={ handleInputClick }
      onInputChange={ handleInputChange }
      onAccessLvlChange={ onAccessLvlChange }
      member={ member }
      allowedAccessLevels={ allowedAccessLevels }
    />
  ) : (
    <DefaultMemberField
      inputId={ inputId }
      required={ restProps.required }
      accessLvl={ member.role }
      onInputClick={ handleInputClick }
      onInputChange={ handleInputChange }
      onAccessLvlChange={ onAccessLvlChange }
      member={ member }
      inviteOptimizationVariant={ inviteOptimizationVariant }
      allowedAccessLevels={ allowedAccessLevels }
    />
  );

};

export namespace InviteMemberTypes {
  export type InputChangeFn = (inputId: string, member: Member) => void
  export type InputClickFn = (member: Member) => void
}

const LegacyMemberField: React.FC<MemberFieldVariantProps> = ({
  inputId,
  required,
  accessLvl,
  onInputClick,
  onInputChange,
  onAccessLvlChange,
  member,
  allowedAccessLevels,
}) => {
  const { control, formState: { errors, touchedFields }} = useFormContext();
  const isTouched = touchedFields[inputId];
  return (
    <div className={ classNames(styles.InviteMember, 'd-flex') }>
      <div className={ styles.InviteMember__InputArea }>
        <div className={ classNames(styles.InviteMember__InputArea__BorderWrap, 'd-flex') }>
          <MuiPersonIcon color="info" />
          <MuiFormControl
            variant="standard"
            css={ muiFormControlStyles.root }
            error={ errors[inputId]?.type === 'async' || errors[inputId]?.type === 'pattern' }
            fullWidth>
            <MuiInputLabel htmlFor={ inputId }>Email address</MuiInputLabel>
            <Controller
              name={ inputId }
              control={ control }
              rules={{ required, pattern: EMAIL_PATTERN_REGEX }}
              defaultValue={ member?.email }
              render={ ({ field }) => (
                <MuiInput
                  { ...field }
                  id={ inputId }
                  type="email"
                  onClick={ onInputClick }
                  onChange={ onInputChange }
                />) }
            />
          </MuiFormControl>
        </div>
        { errors[inputId]?.type === 'async' &&
          <MuiFormHelperText error sx={{ fontSize: '10px', padding: '0 12px' }}>
            { errors[inputId]?.message as string }
          </MuiFormHelperText>
        }
        { errors[inputId]?.type === 'pattern' && isTouched &&
          <MuiFormHelperText error sx={{ fontSize: '10px', padding: '0 12px' }}>
            Please provide a valid email
          </MuiFormHelperText>
        }
      </div>
      <div className={ styles.InviteMember__AccessLvl }>
        <p
          className={ classNames(styles.InviteMember__AccessLvl__Title, 'no-mg ft-size-12 color-secondary-brown') }
        >
          Access level
        </p>
        <AccessLevelPicker
          accessLvlValue={ accessLvl }
          variant="buttons"
          onAccessLvlChange={ onAccessLvlChange }
          allowedAccessLevels={ allowedAccessLevels }
        />
      </div>
    </div>
  );
};

const DefaultMemberField: React.FC<MemberFieldVariantProps> = ({
  inputId,
  accessLvl,
  onInputClick,
  onInputChange,
  onAccessLvlChange,
  member,
  inviteOptimizationVariant,
  allowedAccessLevels,
}) => {
  const { t } = useTranslation();
  const { control, formState, getFieldState, formState: { errors, touchedFields }} = useFormContext();
  const isTouched = touchedFields[inputId];
  const showValidationIcons =
    inviteOptimizationVariant === 'B' || inviteOptimizationVariant === 'C';

  return (
    <FlexBox gap={ 1.5 } flexWrap="wrap">
      <FlexBox flexDirection="column" flex="1 1 auto">
        <Controller
          name={ inputId }
          control={ control }
          defaultValue={ member?.email }
          rules={{ pattern: EMAIL_PATTERN_REGEX }}
          render={ ({ field }) => {
            const value = typeof field.value === 'string' ? field.value : '';
            const hasValue = value.trim().length > 0;
            // Match LoadingButton enablement: same field errors RHF uses for `isValid` (not "no helper text yet").
            const { error: fieldError } = getFieldState(inputId, formState);
            const showCheck = showValidationIcons && hasValue && !fieldError;
            const showErrorIcon = showValidationIcons && hasValue && Boolean(fieldError);

            return (
              <TextField
                { ...field }
                error={ errors[inputId]?.type === 'async' || (errors[inputId]?.type === 'pattern' && isTouched) }
                id={ inputId }
                type="email"
                variant="filled"
                onClick={ onInputClick }
                onChange={ onInputChange }
                label={ t('onboarding:invite_members_step.email_address') }
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MuiEmailIcon color="info" /> </InputAdornment>,
                  endAdornment: showCheck || showErrorIcon ? (
                    <InputAdornment
                      position="end"
                      sx={{ marginTop: '0 !important' }}
                    >
                      <EmailValidationStatusAdornment status={ showErrorIcon ? 'invalid' : 'valid' } />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            );
          } }
        />

        { errors[inputId]?.type === 'async' &&
          <MuiFormHelperText error sx={{ fontSize: '10px', padding: '0 12px' }}>
            { errors[inputId]?.message as string }
          </MuiFormHelperText>
        }
        { errors[inputId]?.type === 'pattern' && isTouched &&
          <MuiFormHelperText error sx={{ fontSize: '10px', padding: '0 12px' }}>
            Please provide a valid email
          </MuiFormHelperText>
        }
      </FlexBox>
      <AccessLevelPicker
        variant="select"
        accessLvlValue={ accessLvl }
        onAccessLvlChange={ onAccessLvlChange }
        allowedAccessLevels={ allowedAccessLevels }
      />
    </FlexBox>
  );
};


